import { ReactElement, useMemo } from 'react';
import { IdentityProvider, GetOrganizationResponse } from '@localstack/types';
import { Chip, Divider, Grid, List, ListItemText, Switch, CircularProgress, Typography, Button } from '@mui/material';
import { LoadingFragment } from '@localstack/ui';

import { IdpPaper, IdpListItem, ScimToggleBox, FullWidthGridItem, ViewScimButton } from './styled';
import { IDP_SCIM_SKELETON_HEIGHT, IDP_SKELETON_HEIGHT } from './types';

type Props = {
  idp: IdentityProvider;
  isSelected: boolean;
  organization?: Optional<GetOrganizationResponse>;
  showScimSection: boolean;
  isIdpLoading: boolean;
  isScimToggleLoading: boolean;
  isAnyScimToggleLoading: boolean;
  existingIdpNames: Set<string>;
  onOpenScimConfig: (idpName: string) => void;
  onToggleScim: (idpName: string, checked: boolean) => Promise<void>;
  onSelectProvider: (idp: IdentityProvider) => void;
};

export const IdpListEntry = ({
  idp,
  organization,
  showScimSection,
  isIdpLoading,
  isScimToggleLoading,
  isAnyScimToggleLoading,
  existingIdpNames,
  onOpenScimConfig,
  onToggleScim,
  onSelectProvider,
}: Props): ReactElement => {
  const otherEnabled = useMemo(() => {
    if (!organization?.scim_settings) return false;
    return Object.entries(organization.scim_settings).some(
      ([name, v]) => v?.is_enabled && name !== idp.idp_name && existingIdpNames.has(name),
    );
  }, [organization?.scim_settings, existingIdpNames, idp.idp_name]);

  const currentEnabled = !!organization?.scim_settings?.[idp.idp_name]?.is_enabled;
  const isStrict = !!organization?.sso_settings?.[idp.idp_name]?.strict_sso_mode;

  return (
    <FullWidthGridItem item xs={12}>
      <IdpPaper>
        <LoadingFragment
          variant="card"
          height={showScimSection ? IDP_SCIM_SKELETON_HEIGHT : IDP_SKELETON_HEIGHT}
          loading={isIdpLoading}
          arrayData={idp ? [idp] : []}
        >
          <List disablePadding sx={{ width: '100%' }}>
            <IdpListItem>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs>
                  <ListItemText
                    primary={idp.idp_name}
                    secondary={idp.provider_type}
                    primaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'body2' }}
                  />
                </Grid>
                {isStrict && (
                  <Grid item>
                    <Chip color="warning" label="Strict SSO Mode" size="small" />
                  </Grid>
                )}
                <Grid item>
                  <Button variant="text" onClick={() => onSelectProvider(idp)}>
                    Configure
                  </Button>
                </Grid>
              </Grid>
            </IdpListItem>

            {showScimSection && (
              <>
                <Divider />
                <IdpListItem disableGutters>
                  <Grid container alignItems="center">
                    <Grid item xs={12} sm={7}>
                      <Typography variant="body1" fontWeight={500}>
                        SCIM User Provisioning
                      </Typography>
                      <ViewScimButton
                        variant="text"
                        size="small"
                        disableRipple
                        onClick={() => onOpenScimConfig(idp.idp_name)}
                      >
                        View SCIM Configuration
                      </ViewScimButton>
                    </Grid>

                    <Grid item xs={12} sm={5} sx={{ mt: { xs: 1, sm: 0 } }}>
                      <ScimToggleBox>
                        {isScimToggleLoading && <CircularProgress size={20} />}
                        <Switch
                          disabled={isScimToggleLoading || isAnyScimToggleLoading || (!currentEnabled && otherEnabled)}
                          checked={currentEnabled}
                          onChange={async (e) => {
                            await onToggleScim(idp.idp_name, e.target.checked);
                          }}
                          color="primary"
                        />
                      </ScimToggleBox>
                    </Grid>
                  </Grid>
                </IdpListItem>
              </>
            )}
          </List>
        </LoadingFragment>
      </IdpPaper>
    </FullWidthGridItem>
  );
};
